// /admin and its children (E12-S6 builds each). The Admin link is shown only to a tenant
// administrator; anyone else who types the address gets "not available", and the API still decides
// what is allowed. The role check is the hub's guard (src/auth/guard.tsx).
import type { Principal } from "@waypoint/contracts";
import { Route, Switch } from "wouter";
import { RequireRole } from "../auth/guard.js";
import { t, type PlainKey } from "../i18n/t.js";
import { NotFoundPage } from "./not-found.js";
import { EmptyState, Page } from "./page.js";

/** The admin sections, by path under /admin (E12-S6-T1 … T5). */
export const ADMIN_SECTIONS: readonly { readonly path: string; readonly title: PlainKey }[] = [
  { path: "policy", title: "page.admin.policy" },
  { path: "bindings", title: "page.admin.bindings" },
  { path: "packs", title: "page.admin.packs" },
  { path: "audit", title: "page.admin.audit" },
  { path: "usage", title: "page.admin.usage" },
  { path: "egress", title: "page.admin.egress" },
];

function ForbiddenPage() {
  return (
    <Page title={t("page.forbidden.title")}>
      <EmptyState icon="lock" title={t("page.forbidden.line")} />
    </Page>
  );
}

/** Rendered inside <Route path="/admin" nest>: paths here are relative to /admin. */
export function AdminRoutes({ principal }: { principal: Principal }) {
  return (
    <RequireRole principal={principal} anyOf={["tenant-admin"]} fallback={<ForbiddenPage />}>
      <Switch>
        <Route path="/">
          <Page title={t("page.admin.title")}>
            <EmptyState
              icon="lock"
              title={t("page.admin.empty")}
              line={t("page.admin.emptyLine")}
            />
          </Page>
        </Route>
        {ADMIN_SECTIONS.map((section) => (
          <Route key={section.path} path={`/${section.path}`}>
            <Page title={t(section.title)}>
              <EmptyState icon="lock" title={t("page.admin.sectionLine")} />
            </Page>
          </Route>
        ))}
        <Route>
          <NotFoundPage />
        </Route>
      </Switch>
    </RequireRole>
  );
}
